// @flow weak

import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { routeTo } from 'redux-router-kit';
import { toModifiers } from 'bemify-js';

import { SIGNUP_PRE_INTENT_EVENT } from '@zapier/tracking';

import * as Account from 'app/entities/Account';
import * as Plan from 'app/entities/Plan';
import * as Profile from 'app/entities/Profile';
import * as RouterSelectors from 'app/common/utils/RouterSelectors';
import AccountActions from 'app/account/AccountActions';
import HeaderAuth from 'app/viewport/components/Header/HeaderAuth';
import HeaderIFrame from 'app/viewport/components/Header/HeaderIFrame';
import HeaderLogoOnly from 'app/viewport/components/Header/HeaderLogoOnly';
import TrackActions from 'app/track/TrackActions';
import WithAccountAccess from 'app/team/components/WithAccountAccess';
import WithCurrentUserInfo from 'app/common/components/WithCurrentUserInfo';
import headerMediaQueries from 'app/viewport/components/Header/HeaderBlock.scss';
import matchesMediaQuery from 'app/common/components/matchesMediaQuery';
import maybeRender from 'app/common/components/maybeRender';
import { BASE_APP_URL_LEGACY } from 'app/developer-v3/utils/constants';
import { getInUseServices } from 'app/dashboard/Selectors';
import { isProductionEnv } from 'app/common/CommonUtils';
import { isSubUrlOf } from 'app/common/utils/UrlUtils';
import { shouldHeldTasksBannerBeVisible } from 'app/viewport/Selectors';
import { trackEvent } from 'app/track/trackingFunctions';
import { Header, megaMenuLinks } from 'app/viewport/components/Header';

import PlatformHeaderContainer from './PlatformHeaderContainer';

import type { HeaderMediaQueries } from 'app/viewport/components/Header/HeaderDefault';

let hasWarnedAboutHeader = false;

const defaultNavigationActions = [
  {
    title: 'Log In',
    href: '/app/login/',
    kind: toModifiers({ divider: true, 'secondary-button': true }),
    relatedMatches: [],
    isVisible: ({ isLoggedIn }) => !isLoggedIn,
  },
  {
    title: 'Sign Up',
    href: '/sign-up/',
    kind: toModifiers({ 'cta-button': true }),
    relatedMatches: [],
    onClick: () => {
      trackEvent(SIGNUP_PRE_INTENT_EVENT, {
        signupPosition: 'header',
      });
    },
    isVisible: ({ isLoggedIn }) => {
      return !isLoggedIn;
    },
  },
];

const inferHeaderConfig = (currentUrl, params, state, queryParams) => {
  // Since currentUrl is set asyncronously and certain pages use route fetch,
  // it can take awhile to resolve causing a flicker in the Header. Therefore,
  // we'll just default to whatever the current location before it resolves.
  // This will become unnecessary when SSR takes over.
  if (!currentUrl) currentUrl = window.location.pathname;

  switch (true) {
    case isSubUrlOf(currentUrl, '/google-sso/onetap-verify'):
    case isSubUrlOf(currentUrl, '/app/login'):
      return {
        CustomComponent: HeaderAuth,
        navigationActions: queryParams.reauth
          ? []
          : [
              {
                title: 'Sign Up',
                href: '/sign-up/',
                kind: toModifiers({ 'secondary-button': true, divider: true }),
                isVisible: _.stubTrue,
              },
            ],
      };

    case isSubUrlOf(currentUrl, '/sign-up'):
      return {
        CustomComponent: HeaderAuth,
        navigationActions: [
          {
            title: 'Log In',
            href: '/app/login/',
            kind: toModifiers({ 'secondary-button': true, divider: true }),
            isVisible: _.stubTrue,
          },
        ],
      };

    case isSubUrlOf(currentUrl, '/oauth/authorize'):
      return {
        CustomComponent: HeaderIFrame,
      };

    case isSubUrlOf(currentUrl, '/app/onboarding'):
      return {
        CustomComponent: HeaderLogoOnly,
      };

    case isSubUrlOf(currentUrl, '/developer'):
      return {
        CustomComponent: PlatformHeaderContainer,
      };

    default:
      return {
        isFullWidth: false,
        showServicesSearch: true,
        badgeText: null,
        headerLinks: [
          {
            title: 'Home',
            href: '/app/dashboard/',
            kind: toModifiers({ selectable: true }),
            // `relatedMatches` will enable `active` link styling when on a matched
            // route. In this case, it highlights for all the subnav pages.
            relatedMatches: [
              '/app/connections/',
              '/app/zaps/',
              '/app/history/',
            ],
            isVisible: ({ isLoggedIn }) => isLoggedIn,
          },
          {
            title: 'Apps',
            href: '/apps', // no trailing slash to avoid redirect
            kind: toModifiers({ selectable: true }),
            relatedMatches: ['/apps/categories/'],
            isVisible: _.stubTrue,
            childLinks: _.find(
              megaMenuLinks,
              collection => collection.key === '/apps'
            ),
          },
          {
            title: 'Explore',
            href: '/apps/integrations/',
            kind: toModifiers({ selectable: true }),
            relatedMatches: [],
            isVisible: _.stubTrue,
            childLinks: _.find(
              megaMenuLinks,
              collection => collection.key === '/apps/integrations'
            ),
          },
          {
            title: 'Tips & Advice',
            href: '/blog/',
            kind: toModifiers({ selectable: true }),
            relatedMatches: ['/learn/'],
            isVisible: _.stubTrue,
            childLinks: _.find(
              megaMenuLinks,
              collection => collection.key === '/blog'
            ),
          },
          {
            title: 'Pricing',
            href: '/pricing', // no trailing slash to avoid redirect
            kind: toModifiers({ selectable: true }),
            relatedMatches: [],
            // Only show if user is in ABTest
            isVisible: ({ isLoggedIn }) => !isLoggedIn,
          },
        ],
        navigationActions: [
          {
            title: 'Join Team',
            href: '/app/settings/team/',
            kind: toModifiers({ divider: true, 'secondary-button': true }),
            relatedMatches: [],
            isVisible: ({ isLoggedIn, hasJoinableAccount, mediaQueries }) => {
              // Hide this link when on tablet sized media query
              const isOnTabletView = Boolean(
                mediaQueries.tabletMediaQuery && !mediaQueries.desktopMediaQuery
              );

              return isLoggedIn && !isOnTabletView && hasJoinableAccount;
            },
          },
          {
            title: 'Invite Team',
            href: '/app/settings/team/',
            kind: toModifiers({ divider: true, 'secondary-button': true }),
            relatedMatches: [],
            isVisible: ({
              isLoggedIn,
              hasJoinableAccount,
              hasTeamAccount,
              mediaQueries,
            }) => {
              // Hide this link when on tablet sized media query
              const isOnTabletView = Boolean(
                mediaQueries.tabletMediaQuery && !mediaQueries.desktopMediaQuery
              );

              return (
                isLoggedIn &&
                !hasJoinableAccount &&
                !isOnTabletView &&
                hasTeamAccount
              );
            },
          },
          {
            title: 'Upgrade Now',
            href: '/app/billing/plans/',
            kind: toModifiers({ divider: true, 'secondary-button': true }),
            relatedMatches: [],
            isVisible: ({ isLoggedIn, hasTeamAccount, mediaQueries }) => {
              // Hide this link when on tablet sized media query
              const isOnTabletView = Boolean(
                mediaQueries.tabletMediaQuery && !mediaQueries.desktopMediaQuery
              );

              return isLoggedIn && !isOnTabletView && !hasTeamAccount;
            },
          },
          {
            title: 'Make a Zap!',
            href: '/app/editor/',
            kind: toModifiers({ 'cta-button': true }),
            relatedMatches: [],
            isVisible: ({ isLoggedIn }) => isLoggedIn,
          },
          ...defaultNavigationActions,
        ],
      };
  }
};

// Some URLs that require server side apps break when the new header is used.
// For example, previously the blog did not render a header. Now that it does,
// when we attempt to navigate to one of the header links, it will inject the
// React app _below_ the current blog content (there is a larger issues here
// that is outside the context of this work). So you end up in a situation like
// this: http://bit.ly/2nYb2J7
// To mitigate this issue in the short term, we need to force the client to
// refresh the page when exiting these routes.
const shouldForceRefreshOnExit = currentUrl => {
  const predicates = [url => isSubUrlOf(url, '/blog')];

  return predicates.some(predicate => predicate(currentUrl));
};

// Some URLs need to hide the subnavigation, the listed URLs will hide the
// subnav. Ideally this would be controlled explicitly through the usage of
// the <Header /> component on each page. For now this hack exists instead!
const shouldSubNavBeVisible = (currentUrl: string, queryParams: Object) => {
  const predicates = [
    url => isSubUrlOf(url, '/app') && !isSubUrlOf(url, '/app/billing/plans'),
    (url, params) =>
      isSubUrlOf(url, '/apps/integrations') && params.minimal !== 'true',
  ];

  return predicates.some(predicate => predicate(currentUrl, queryParams));
};

// Some urls should hide the header because it is a waste of resources if we
// attempt to render it (because it will make async requests). Previously we
// managed this by deciding to render the header on a Django template. When
// the client then moved to a page that did not contain a header (because we
// need an injection point), `refreshChrome` would create a `#header` element
// and inject the header there. We've moved this up to `Root.js` using a portal
// combined with `<EnsureElement>`.
// The problem now is that the Header will always render. So this logic below
// replicates what we previously did by discluding the header in a Django
// template.
export const shouldHeaderBeVisible = currentUrl => {
  const predicates = [
    url => !isSubUrlOf(url, '/app/editor'),
    url => !isSubUrlOf(url, '/app/shared'),
    url => !isSubUrlOf(url, '/engine/auth/start'),
    url =>
      !(
        isSubUrlOf(url, `${BASE_APP_URL_LEGACY}/app`) &&
        _.endsWith(url, 'json-editor')
      ),
    url =>
      !(
        isSubUrlOf(url, `${BASE_APP_URL_LEGACY}/app`) &&
        _.endsWith(url, 'scripting')
      ),
    url => !isSubUrlOf(url, '/app/settings-v2'),
  ];

  return predicates.every(predicate => predicate(currentUrl));
};

// The mobile menu enables a very different DOM structure and a11y props. We
// have two media queries as a result of the content that displays when a user
// is logged in vs. logged out so this increased complexity slightly.
const shouldEnableMobileMenu = ({
  isLoggedIn,
  mediaQueries,
}: {
  isLoggedIn: boolean,
  mediaQueries: HeaderMediaQueries,
}) => {
  return (
    (mediaQueries.mobileLoggedOutMediaQuery && !isLoggedIn) ||
    (mediaQueries.mobileLoggedInMediaQuery && isLoggedIn)
  );
};

const mapStateToProps = (state, props) => {
  const currentUrl = RouterSelectors.currentUrl(state) || '';
  const currentPathname = RouterSelectors.getCurrentPathname(state) || '';
  const queryParams = RouterSelectors.getQueryParams(state) || {};
  const params = RouterSelectors.getParams(state) || {};
  const isLoggedIn = Profile.selectors.all.isLoggedIn(state);
  const hasAnyTeam = Account.selectors.all.hasAnyTeam(state);
  const headerConfig = inferHeaderConfig(
    currentUrl,
    params,
    state,
    queryParams
  );

  const {
    badgeText = '',
    headerLinks = [],
    isFullWidth = false,
    navigationActions = [],
    showServicesSearch = false,
    CustomComponent,
  } = headerConfig;

  const links = headerLinks
    .filter(item =>
      item.isVisible({
        isLoggedIn,
        hasTeamAccount: hasAnyTeam,
        mediaQueries: props.matchesMediaQuery,
        state,
      })
    )
    .map(item => {
      // TODO(neilff): For the initial roll out of the mega menu, we will only
      // be showing it to only logged out users. This will allow us to get
      // feedback and iterate as well as buy us time to customize the
      // experience for logged in users.
      // This mechanism can be removed once we wish to ship the mega menu
      // to logged in users.
      if (isLoggedIn) {
        return {
          ...item,
          childLinks: [],
        };
      }

      return item;
    });

  const actions = navigationActions.filter(item =>
    item.isVisible({
      isLoggedIn,
      hasJoinableAccount: props.hasJoinableAccount,
      hasTeamAccount: hasAnyTeam,
      mediaQueries: props.matchesMediaQuery,
    })
  );

  return {
    CustomComponent,
    actions,
    badgeText,
    currentUrl,
    forceRefreshOnExit: shouldForceRefreshOnExit(currentUrl),
    inUseServices: getInUseServices(state),
    isFullWidth,
    isHeaderVisible: shouldHeaderBeVisible(currentUrl),
    isHeldTasksBannerVisible: shouldHeldTasksBannerBeVisible(currentPathname),
    isLoggedIn,
    isMobileMenuEnabled: shouldEnableMobileMenu({
      isLoggedIn,
      mediaQueries: props.matchesMediaQuery,
    }),
    isSubNavVisible: shouldSubNavBeVisible(currentUrl, queryParams),
    links,
    numberOfApps: _.get(window, ['BASE_CONFIG', 'numberOfApps'], 0),
    showServicesSearch,
  };
};

const mapDispatchToProps = dispatch => ({
  onMount() {
    dispatch(AccountActions.loadAccount()).then(() => {
      dispatch(Plan.actions.loadCollection());
    });
  },
  onRouteToService(serviceSlug) {
    dispatch(routeTo(`/apps/${serviceSlug}/integrations/`));
  },
  onTrack(message, data) {
    dispatch(TrackActions.track(message, data));
  },
});

const ReduxContainer = _.flow(
  maybeRender(({ isHeaderVisible, hasHeaderFromServer, currentUrl }) => {
    // This condition covers a situation where we don't want to render the
    // header, but we also cannot currently check if the URL is in our
    // whitelist (see `shouldHeaderBeVisible`) because we don't have the current
    // url information yet. This happens because `currentUrl` is asynchronous
    // so on startup it does not have a value.
    // A possible fix for this hack would be to have the server send us the
    // current location of the client, this would be closer to what real SSR
    // would do.
    // This fix prevents the header from rendering and then disappearing on
    // our whitelisted pages.
    if (_.isEmpty(currentUrl) && hasHeaderFromServer === false) {
      if (!isProductionEnv() && hasWarnedAboutHeader === false) {
        hasWarnedAboutHeader = true;

        console.warn(
          'Heads up! The header was not rendered on this page when the app started up because it did not meet the criteria to do so. If you believe this is incorrect, you may need to include the header in the Django template for this page. See `app/viewport/components/HeaderContainer.js` for more details.'
        );
      }

      return false;
    }

    return isHeaderVisible === true;
  }),
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  matchesMediaQuery(headerMediaQueries)
)(Header);

const HeaderContainer = () => {
  return (
    <WithCurrentUserInfo>
      {({ isLoggedIn }) => (
        <WithAccountAccess skip={!isLoggedIn}>
          {({ joinableAccounts }) => (
            <ReduxContainer
              hasJoinableAccount={
                Array.isArray(joinableAccounts) && joinableAccounts.length > 0
              }
            />
          )}
        </WithAccountAccess>
      )}
    </WithCurrentUserInfo>
  );
};

export default HeaderContainer;
